import { useMemo } from 'react'

import { useRequest } from 'ahooks'
import { Cascader } from 'antd'
import { isNil } from 'lodash'

import { RegionEntity } from '@eastcoal/api/system/interface'
import { queryRegionTreeApi } from '@eastcoal/api/system/region/queryRegionTree'

import { DataError } from '@eastcoal/common/components/DataError'
import { omit } from '@eastcoal/common/utils/object'

import { RegionCascaderProps } from './interface'

const fieldNames = {
  value: 'id',
  label: 'name',
  children: 'children'
} as const

export const RegionCascader = ({
  value,
  onChange,
  ...restProps
}: RegionCascaderProps) => {
  const { data, error, loading, refresh } = useRequest(queryRegionTreeApi, {
    manual: false,
    defaultParams: [
      {
        enable: true
      }
    ]
  })

  const selectedKeys = useMemo(() => {
    if (
      isNil(value) ||
      isNil(value.province?.id) ||
      isNil(value.city?.id) ||
      isNil(value.district?.id)
    ) {
      return undefined
    }

    return [value.province.id, value.city.id, value.district.id] as string[]
  }, [value])

  const handleChange = (_: unknown, options: RegionEntity[]) => {
    onChange?.(
      options
        ? {
            province: omit(options[0], ['children']),
            city: omit(options[1], ['children']),
            district: omit(options[2], ['children'])
          }
        : undefined
    )
  }

  return error ? (
    <DataError error={error} loading={loading} reload={refresh} />
  ) : (
    <Cascader
      changeOnSelect={false}
      fieldNames={fieldNames}
      loading={loading}
      multiple={false}
      options={data}
      value={selectedKeys}
      onChange={handleChange}
      {...restProps}
    />
  )
}
